<template>
    <div class="quick-contact-container">
      <button
        @click="togglePopup"
        class="contact-button btn rounded-circle p-3 shadow-lg"
        :class="{ 'is-active': showPopup, 'glass-effect': true }"
        aria-label="Open quick contact form"
      >
        <i :class="showPopup ? 'fas fa-times' : 'fas fa-comment-dots'"></i>
      </button>
  
      <Transition name="slide-up-fade">
        <div v-if="showPopup" class="contact-popup shadow-xl rounded-4 p-4" :class="{ 'glass-effect': true }">
          <ContactForm 
            :isPopup="true" 
            @form-submitted="onFormSubmitted" 
          />
          <i class="close-icon fas fa-times" @click="togglePopup"></i>
        </div>
      </Transition>
      
      <Transition name="fade">
        <div v-if="formSuccess" class="success-overlay rounded-4 p-4">
          <h5 class="text-success pb-2"><i class="fas fa-check-circle me-2"></i>Success!</h5>
          <p class="small text-muted">Thank you! We've received your enquiry and will be in touch shortly.</p>
          <button @click="closeSuccess" class="btn btn-sm btn-outline-dark mt-3">Close</button>
        </div>
      </Transition>
    </div>
  </template>
  
  <script setup lang="ts">
  import { ref } from 'vue';
  import ContactForm from "@/components/general/ContactForm.vue"; // Adjust path as needed
  
  const showPopup = ref(false);
  const formSuccess = ref(false);
  
  const togglePopup = () => {
    if (formSuccess.value) {
      // If successful message is showing, close it first
      closeSuccess();
    }
    showPopup.value = !showPopup.value;
  };
  
  const onFormSubmitted = (isSuccess: boolean) => {
    if (isSuccess) {
      formSuccess.value = true;
      // Optional: Auto-hide the form/show success, but keep the widget open temporarily
    }
  };
  
  const closeSuccess = () => {
      formSuccess.value = false;
      showPopup.value = false;
  }
  </script>
  
  <style scoped lang="scss">
  
  /* --- BASE CONTAINER --- */
  .quick-contact-container {
    position: fixed;
    bottom: 20px;
    right: 20px;
    z-index: 1000; /* Ensure it is above almost everything */
    display: flex;
    flex-direction: column;
    align-items: flex-end;
  }
  
  /* --- GLASS EFFECT (Based on your home banner) --- */
  .glass-effect {
    background: rgba(255, 255, 255, 0.6);
    -webkit-backdrop-filter: blur(8px);
    backdrop-filter: blur(8px);
    border: 1px solid rgba(255, 255, 255, 0.3);
  }
  
  /* --- CONTACT BUTTON --- */
  .contact-button {
    width: 60px;
    height: 60px;
    color: $dark;
    border: none;
    font-size: 1.5rem;
    transition: transform 0.3s ease, background-color 0.3s ease;
    z-index: 1001;
    display: flex;
    align-items: center;
    justify-content: center;
    
    &.is-active {
      background-color: $dark; /* Darken when open */
      color: white;
      transform: rotate(45deg); /* Spin the X icon */
    }
  
  }
  
  /* --- POPUP FORM --- */
  .contact-popup {
    position: absolute;
    bottom: 80px; /* Position above the button */
    right: 0;
    width: 90vw; /* Default for small screens */
    max-width: 380px; /* Max size for larger screens */
    min-height: 400px;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
    transform-origin: bottom right;
  }
  
  .close-icon {
      position: absolute;
      top: 15px;
      right: 15px;
      font-size: 1.2rem;
      cursor: pointer;
      color: $dark;
      z-index: 10;
  }
  
  /* --- SUCCESS MESSAGE --- */
  .success-overlay {
      position: absolute;
      bottom: 80px; 
      right: 0;
      width: 90vw;
      max-width: 380px;
      min-height: 400px;
      background-color: white;
      box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      text-align: center;
  }
  
  /* --- TRANSITIONS --- */
  .slide-up-fade-enter-active,
  .slide-up-fade-leave-active {
    transition: all 0.3s ease-out;
  }
  
  .slide-up-fade-enter-from,
  .slide-up-fade-leave-to {
    transform: translateY(20px) scale(0.9);
    opacity: 0;
  }
  
  .fade-enter-active,
  .fade-leave-active {
    transition: opacity 0.3s ease;
  }
  
  .fade-enter-from,
  .fade-leave-to {
    opacity: 0;
  }
  </style>